import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone, Optional, SkipSelf } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { FirebaseCustomError } from 'src/app/shared/classes/firebaseCustomError';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class SentryService {
  constructor(
    private zone: NgZone,
    @Optional() @SkipSelf() thisService?: SentryService
  ) {
    if (thisService) throw new Error('Multiple instances of SentryService');
  }

  public static init(): void {
    Sentry.init({
      dsn: environment.sentryDSN,
      // TODO: @Aandrzejmi sentry zmieniło opcje browser tracing.
      // https://docs.sentry.io/platforms/javascript/tracing/instrumentation/automatic-instrumentation/#configuration-options
      integrations: [Sentry.browserTracingIntegration()],
      environment: environment.envName,
      release: environment.gitSha,
      tracesSampleRate: 1.0
    });
  }

  public setUser(id: string | null): void {
    // ? sentry replaces ip_address: '{{auto}}' with actual client ip
    Sentry.setUser(id ? { id, ip_address: '{{auto}}' } : null);
  }

  public setContext(context: { estateId?: string; companyId?: string } | null): void {
    Sentry.setTag('estate', context?.estateId || null);
    Sentry.setTag('company', context?.companyId || null);
  }

  public captureError(candidate: unknown): void {
    this.zone.runOutsideAngular(() => {
      Sentry.captureException(this.extractError(candidate) || 'Handled unknown error');
    });
  }

  // pulled from Sentry default implementation
  // https://github.com/getsentry/sentry-javascript/blob/master/packages/angular/src/errorhandler.ts
  private extractError(errorCandidate: unknown): unknown {
    let error = errorCandidate;

    // Try to unwrap zone.js error.
    // https://github.com/angular/angular/blob/master/packages/core/src/util/errors.ts
    if (error && (error as { ngOriginalError: Error }).ngOriginalError) {
      error = (error as { ngOriginalError: Error }).ngOriginalError;
    }

    // We can handle messages and Error objects directly.
    if (typeof error === 'string' || error instanceof Error) {
      return error;
    }

    // If it's http module error, extract as much information from it as we can.
    if (error instanceof HttpErrorResponse) {
      // The `error` property of http exception can be either an `Error` object, which we can use directly...
      if (error.error instanceof Error) {
        return error.error;
      }

      // ... or an`ErrorEvent`, which can provide us with the message but no stack...
      if (error.error instanceof ErrorEvent && error.error.message) {
        return error.error.message;
      }

      // ...or the request body itself, which we can use as a message instead.
      if (typeof error.error === 'string') {
        return `Server returned code ${error.status} with body "${error.error}"`;
      }

      // If we don't have any detailed information, fallback to the request message itself.
      return error.message;
    }

    // https://docs.sentry.io/product/issues/issue-details/breadcrumbs/#breadcrumbs-display
    // TODO find way to display objects in sentry
    if (error instanceof FirebaseCustomError) {
      return this.extractError(error.error);
    }

    // Nothing was extracted, fallback to default error message.
    return null;
  }
}
